import { Box, CircularProgress, IconButton, Typography } from '@mui/material'
import {
  Close as CloseIcon,
  LabelOutlined as KindIcon,
  PictureAsPdfOutlined as PdfIcon,
} from '@mui/icons-material'

import {
  PillSelect,
  type PillOption,
} from '@/apps/latest/components/molecules/PillSelect'
import { colorTokens } from '@/apps/latest/shared/styles/colorTokens'
import { RADIUS } from '@/shared/styles/radii'

import { type SendingItem } from './attachmentUploads'
import { noteSx, textActionSx } from './attachmentStyles'
import { openPdf } from './openPdf'
import {
  isPdfUrl,
  type RecordingImage,
  type RecordingImageKind,
} from './recordingImageApi'

// 資料の一覧の 1 件 1 行 (design-book 02-components/attachments-panel.md「1 件 1 行」)。
// 並びは 絵 → 種別の文字 → ✕ (物 = 紙が先、属性は後。hub log/108 決定 42)。写真は 32px のサムネ (押すと拡大)、
// PDF は文書の絵だけ (押すと新しいタブ)。種別は capsule と ▾ を持たない文字の釦 (Notion のプロパティの値の形。
// capsule + ▾ だと隣の toggle 行「確認事項 N 件 ∨」と同じ形に見え、∨ の意味も違う)。付けた後に任意で選ぶ。
// 時刻は出さない。行の高さは隣の行 (確認事項・改善提案) と同じ刻み。✕ は右端に常時。

const THUMB = 32

const rowSx = {
  display: 'flex',
  alignItems: 'center',
  gap: 1,
  minHeight: THUMB,
} as const

const thumbSx = {
  width: THUMB,
  height: THUMB,
  flexShrink: 0,
  borderRadius: `${RADIUS.sm}px`,
  bgcolor: colorTokens.bg.subtle,
} as const

interface RowProps {
  image: RecordingImage
  // 種別の一覧。渡したときだけ行に種別の pill (種別を持つ系統だけ)
  kinds?: PillOption<RecordingImageKind>[]
  onKindChange: (kind: RecordingImageKind) => void
  // 写真の拡大
  onOpen: () => void
  // 消す口。読むだけの置き場 (別の回の資料) は渡さず、✕ を出さない
  onDelete?: () => void
  deleting: boolean
}

export const AttachmentRow = ({
  image,
  kinds,
  onKindChange,
  onOpen,
  onDelete,
  deleting,
}: RowProps) => (
  <Box sx={{ ...rowSx, opacity: deleting ? 0.5 : 1 }}>
    {isPdfUrl(image.image) ? (
      <Box
        component="button"
        type="button"
        aria-label="PDF を開く"
        onClick={() => openPdf(image.image)}
        sx={{
          ...thumbSx,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          p: 0,
          border: 'none',
          color: colorTokens.fg.muted,
          cursor: 'pointer',
          '&:hover': { color: colorTokens.fg.default },
        }}
      >
        <PdfIcon sx={{ fontSize: 20 }} />
      </Box>
    ) : (
      <Box
        component="button"
        type="button"
        aria-label="資料を拡大"
        onClick={onOpen}
        sx={{ ...thumbSx, p: 0, border: 'none', cursor: 'pointer' }}
      >
        <Box
          component="img"
          src={image.image}
          alt="資料"
          sx={{
            ...thumbSx,
            display: 'block',
            objectFit: 'cover',
          }}
        />
      </Box>
    )}
    {kinds && (
      // 値の前に tag の軸アイコン (design-book pill-select.md「軸表示」)。「未設定」は property の値で、行には
      // property の絵も名前も無く「何の未設定か」が分からなかった。決定 42 で絵を外した理由 (AI の action 行と
      // 同じ形) は AI の行が欄になって当たらない (hub log/108 決定 48)
      <PillSelect
        value={image.kind}
        options={kinds}
        onChange={onKindChange}
        ariaLabel="資料の種別"
        variant="text"
        icon={<KindIcon />}
      />
    )}
    {onDelete && (
      <IconButton
        size="small"
        aria-label="この資料を削除"
        onClick={onDelete}
        disabled={deleting}
        sx={{
          // 右端の列 (確認事項の ∨・足す口の icon) と絵の x を揃える
          ml: 'auto',
          mr: -0.75,
          width: 28,
          height: 28,
          color: colorTokens.fg.muted,
          '&:hover': {
            bgcolor: colorTokens.bg.muted,
            color: colorTokens.state.dangerFg,
          },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
      </IconButton>
    )}
  </Box>
)

interface SendingRowProps {
  item: SendingItem
  onRetry: () => void
  onSave: () => void
}

// 送信中は spinner と「送っています…」、送れなかったら「もう一度」「端末に保存」の 2 つの手 (絵は付けない)
export const SendingRow = ({ item, onRetry, onSave }: SendingRowProps) => {
  if (item.status === 'sending') {
    return (
      <Box role="status" aria-label="送信中の資料" sx={rowSx}>
        <Box
          sx={{
            ...thumbSx,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            color: colorTokens.fg.muted,
          }}
        >
          <CircularProgress size={14} sx={{ color: 'inherit' }} />
        </Box>
        <Typography sx={noteSx}>送っています…</Typography>
      </Box>
    )
  }
  return (
    <Box role="group" aria-label="送れなかった資料" sx={rowSx}>
      <Typography sx={noteSx}>送れませんでした</Typography>
      <Box component="button" type="button" onClick={onRetry} sx={textActionSx}>
        もう一度
      </Box>
      <Typography sx={noteSx}>·</Typography>
      <Box component="button" type="button" onClick={onSave} sx={textActionSx}>
        端末に保存
      </Box>
    </Box>
  )
}
